// --- React & Redux
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
// --- External UI Libraries
import Button from '@dtci/spellbook/Button';
import Select from '@dtci/spellbook/Select';
import Tooltip from '@dtci/spellbook/Tooltip';
// --- API
import { datastageApi } from 'datastage/api';
// --- Hooks & Store
import { useLoading } from 'datastage/utils/hooks/useLoading';
import { useTemplatesActions } from 'datastage/store/templates/useTemplates';
import { useToasts } from 'datastage/utils/hooks/useToasts';
// --- Components
import { InputLabel } from 'datastage/components/InputLabel/InputLabel';
// --- Styles
import './styles.scss';

/**
 * Upload Templates Form
 *
 * Features:
 * - Renders a form for uploading templates
 * - Handles form interactions and data fetching
 *
 * @returns {Element}
 * @constructor
 */
export function UploadTemplateForm() {
    const { updateTemplates, updateTemplatesForm } = useTemplatesActions();
    const { setLoadingTrue, setLoadingFalse } = useLoading();
    const { showErrorToast } = useToasts();
    const {
        uploadForm,
        actionOptions,
        dateFormatOptions,
        entityTypeOptions,
        fieldGroups,
        customFields,
        templateOptions,
    } = useSelector((state) => state.templates);

    // Check if all required template configuration fields are filled
    const isConfigComplete = uploadForm.action && uploadForm.entityType && uploadForm.template && uploadForm.dateFormat;

    // Get tooltip message for disabled field selection buttons
    const getDisabledTooltip = () => {
        if (isConfigComplete) return null;
        const missing = [];
        if (!uploadForm.action) missing.push('Action');
        if (!uploadForm.entityType) missing.push('Module');
        if (!uploadForm.template) missing.push('Template');
        if (!uploadForm.dateFormat) missing.push('Date Format');
        return `Complete required fields: ${missing.join(', ')}`;
    };

    // Fetch template options on component mount
    useEffect(() => {
        const fetchOptions = async () => {
            try {
                const [actions, dateFormats, entityTypes] = await Promise.all([
                    datastageApi.rlTemplateActions(),
                    datastageApi.rlTemplateDateFormats(),
                    datastageApi.rlTemplateEntityTypes(),
                ]);
                updateTemplates({
                    actionOptions: actions,
                    dateFormatOptions: dateFormats,
                    entityTypeOptions: entityTypes,
                });
            } catch (error) {
                console.error('Error fetching template options:', error);
            }
        };
        fetchOptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Fetch fields for custom selection
    const fetchFieldsForCustomSelection = async () => {
        setLoadingTrue();
        try {
            const fetchedFieldGroups = await datastageApi.rlTemplateFields(uploadForm);

            if (fetchedFieldGroups?.code) {
                showErrorToast(fetchedFieldGroups.message || 'Failed to fetch field groups.');
                return false;
            }

            const defaultCustomFields = fetchedFieldGroups
                .flatMap((v) => v.fields)
                .map((v) => v.fieldId);

            const customFieldList = await datastageApi.rlCustomListSearch(uploadForm);

            if (customFieldList?.code) {
                showErrorToast(customFieldList.message || 'Failed to fetch custom field list.');
                return false;
            }

            updateTemplates({
                fieldGroups: fetchedFieldGroups,
                customFields: defaultCustomFields,
                customFieldList: customFieldList,
                selectedCustomList: '',
            });
            return true;
        } catch (error) {
            showErrorToast(error?.message || 'Unexpected error fetching fields.');
            console.error('Unexpected error fetching fields:', error);
            return false;
        } finally {
            setLoadingFalse();
        }
    };

    // Handle switching to "All Fields" mode
    const handleSelectAllFields = () => {
        updateTemplatesForm({
            key: 'uploadForm',
            data: { includeAllFields: true },
        });
    };

    // Handle switching to "Custom Selection" mode - fetch fields and show button
    const handleSelectCustomFields = async () => {
        updateTemplatesForm({
            key: 'uploadForm',
            data: { includeAllFields: false },
        });
        
        // Only fetch if we don't already have field groups loaded
        if (!fieldGroups?.length) {
            await fetchFieldsForCustomSelection();
        }
    };

    // Open the field selection modal
    const handleOpenModal = () => {
        updateTemplates({
            isDrawerOpen: true,
        });
    };

    // Handle template selection change
    // Reset field groups when template changes (they'll be fetched when user selects custom)
    const handleTemplateChange = async (value) => {
        updateTemplatesForm({
            key: 'uploadForm',
            data: { template: value },
        });

        // Clear existing field groups when template changes
        updateTemplates({
            fieldGroups: [],
            customFields: [],
        });

        // If already in custom mode, fetch fields for the new template
        if (value && !uploadForm.includeAllFields) {
            await fetchFieldsForCustomSelection();
        }
    };

    // Handle entity type selection change
    // Reset template and fetch new template options
    const handleEntityTypeChange = async (value) => {
        updateTemplatesForm({
            key: 'uploadForm',
            data: {
                entityType: value,
                template: '',
                includeAllFields: true,
            },
        });
        updateTemplates({
            fieldGroups: [],
            customFields: [],
        });
        if (value && value.value) {
            setLoadingTrue();
            try {
                const templates = await datastageApi.rlTemplatesByType(value.value);
                updateTemplates({
                    templateOptions: templates,
                });
            } catch (error) {
                showErrorToast(error?.message || 'Failed to fetch template options.');
                console.error('Failed to fetch template options:', error);
            } finally {
                setLoadingFalse();
            }
        } else {
            updateTemplates({
                templateOptions: [],
            });
        }
    };

    // Handle Excel file generation
    const handleGenerateExcel = async () => {
        setLoadingTrue();
        try {
            const res = await datastageApi.generateExcelFile(uploadForm, customFields);
            if (res?.code) {
                showErrorToast(res.message || 'Failed to generate Excel file.');
            }
        } catch (error) {
            showErrorToast(error?.message || 'Unexpected error generating Excel file.');
            console.error('Unexpected error generating Excel file:', error);
        } finally {
            setLoadingFalse();
        }
    };

    // Retrieve Rightsline domain based on environment
    const retrieveRLDomain = () => {
        const env = import.meta.env.VITE_ENV;

        switch (env) {
            case 'production':
                return 'app.rightsline.com';
            case 'staging':
                return 'app-staging.rightsline.com';
            case 'qat':
                return 'app-pm.rightsline.com';
            default:
                return 'app-int.rightsline.com';
        }
    };

    // Handle opening saved templates modal
    // Fetch saved templates and update state
    const onOpenSavedTemplates = async () => {
        setLoadingTrue();
        try {
            const data = await datastageApi.savedTemplates();
            if (data?.code) {
                showErrorToast(data.message || 'Failed to fetch saved templates.');
                return;
            }
            updateTemplates({
                isSavedTemplatesOpen: true,
                templates: data,
            });
        } catch (error) {
            showErrorToast(error?.message || 'Unexpected error fetching saved templates.');
            console.error('Unexpected error fetching saved templates:', error);
        } finally {
            setLoadingFalse();
        }
    };

    const handleInputChange = (field, value) => {
        updateTemplatesForm({
            key: 'uploadForm',
            data: { [field]: value },
        });
    };

    return (
        <div className="upload-template-page">
            {/* Section 1: Template Configuration */}
            <div className="upload-card">
                <div className="upload-card__header">
                    Template Configuration
                </div>
                <div className="upload-card__content">
                    <div className="upload-grid">
                        <InputLabel label="Action" required>
                            <Select
                                isSearchable
                                options={actionOptions}
                                onChange={value => handleInputChange('action', value)}
                                value={uploadForm.action}
                            />
                        </InputLabel>
                        <InputLabel label="Module" required>
                            <Select
                                isSearchable
                                options={entityTypeOptions}
                                onChange={handleEntityTypeChange}
                                value={uploadForm.entityType}
                                menuPortalTarget={document.body}
                            />
                        </InputLabel>
                        <InputLabel label="Template" required>
                            <Select
                                isSearchable
                                options={templateOptions}
                                onChange={handleTemplateChange}
                                value={uploadForm.template}
                                menuPortalTarget={document.body}
                            />
                        </InputLabel>
                        <InputLabel label="Date Format" required>
                            <Select
                                isSearchable
                                options={dateFormatOptions}
                                onChange={value => handleInputChange('dateFormat', value)}
                                value={uploadForm.dateFormat}
                                menuPortalTarget={document.body}
                            />
                        </InputLabel>
                    </div>
                </div>
            </div>

            {/* Section 2: Field Selection */}
            <div className="upload-card">
                <div className="upload-card__header">
                    Field Selection
                </div>
                <div className="upload-card__content">
                    <div className="field-selection-container">
                        {!isConfigComplete ? (
                            <Tooltip
                                tippyProps={{ placement: 'bottom' }}
                                tooltipContent={getDisabledTooltip()}
                            >
                                <div className="field-segmented-control">
                                    <button
                                        type="button"
                                        className={`field-segment ${uploadForm.includeAllFields ? 'field-segment--active' : ''}`}
                                        onClick={handleSelectAllFields}
                                        disabled={!isConfigComplete}
                                    >
                                        All Fields
                                    </button>
                                    <button
                                        type="button"
                                        className={`field-segment ${!uploadForm.includeAllFields ? 'field-segment--active' : ''}`}
                                        onClick={handleSelectCustomFields}
                                        disabled={!isConfigComplete}
                                    >
                                        <strong>Custom Selection</strong>
                                    </button>
                                </div>
                            </Tooltip>
                        ) : (
                            <div className="field-segmented-control">
                                <button
                                    type="button"
                                    className={`field-segment ${uploadForm.includeAllFields ? 'field-segment--active' : ''}`}
                                    onClick={handleSelectAllFields}
                                    disabled={!isConfigComplete}
                                >
                                    All Fields
                                </button>
                                <button
                                    type="button"
                                    className={`field-segment ${!uploadForm.includeAllFields ? 'field-segment--active' : ''}`}
                                    onClick={handleSelectCustomFields}
                                    disabled={!isConfigComplete}
                                >
                                    Custom Selection
                                </button>
                            </div>
                        )}
                        
                        {/* Custom selection details */}
                        {!uploadForm.includeAllFields && (
                            <div className="field-selection-details">
                                {fieldGroups?.length > 0 ? (
                                    <>
                                        <span className="field-count" style={{ fontSize: '1.1em' }}>
                                            <strong>{customFields?.length} of {fieldGroups?.flatMap((v) => v.fields)?.length} fields selected</strong>
                                        </span>
                                        <Button
                                            sizeType="medium"
                                            styleType="standard"
                                            onClick={handleOpenModal}
                                        >
                                            <i className="fa-solid fa-pen-to-square" style={{ marginRight: 8 }} />
                                            EDIT FIELDS
                                        </Button>
                                    </>
                                ) : (
                                    <span className="field-loading">Loading fields...</span>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Section 3: Next Steps */}
            <div className="upload-info-banner">
                <i className="fa-solid fa-circle-info" />
                <div className="upload-info-banner__content">
                    <strong>Next Steps:</strong> When you are ready to upload, save as CSV and use Rightsline{' '}
                    <a
                        href={`https://${retrieveRLDomain()}/data-tools/data-import-v2`}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Data Import
                    </a>{' '}
                    for bulk create or{' '}
                    <a
                        href={`https://${retrieveRLDomain()}/data-tools/data-update`}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Data Update
                    </a>{' '}
                    for bulk edit.
                </div>
            </div>

            {/* Action Buttons */}
            <div className="upload-actions">
                <Button
                    sizeType="medium"
                    styleType="standard"
                    onClick={onOpenSavedTemplates}
                >
                    <i className="fa-solid fa-bookmark" style={{ marginRight: 8 }} />
                    MY SAVED TEMPLATES
                </Button>
                <Button
                    sizeType="medium"
                    styleType="standard"
                    onClick={handleGenerateExcel}
                >
                    <i className="fa-solid fa-file-excel" style={{ marginRight: 8 }} />
                    GENERATE EXCEL FILE
                </Button>
            </div>
        </div>
    );
}
